// Adversarial critic: tries to refute every proposed claim, demands evidence,
// and downgrades / rejects weak ones. Pure logic, no DOM.

import { DEFAULT_CONFIG, type ResearchConfig } from "./config";
import type { Claim } from "./types";
import { rand, sleep } from "./util";

export interface CritiqueArgs {
  claims: Claim[];
  signal?: AbortSignal;
  config?: ResearchConfig;
  onClaim?: (c: Claim) => Promise<void> | void;
}

const REFUTATIONS = [
  "no replication beyond original lab",
  "single benchmark, low n",
  "evidence chain has a missing hop",
  "publication is older than 18 months",
  "author affiliation overlaps with vendor",
  "claim relies on simulated rather than real-world data",
  "contradicted by adjacent recent finding",
];

export async function adversarialCritique(args: CritiqueArgs): Promise<Claim[]> {
  const cfg = args.config ?? DEFAULT_CONFIG;
  const out: Claim[] = [];
  for (const c of args.claims) {
    let confidence = c.confidence;
    let rationale = c.rationale ?? "";
    let status: Claim["status"] = "accepted";

    for (let r = 0; r < cfg.critic.refutationRounds; r++) {
      await sleep(rand(40, 140), args.signal);
      // Probability of finding a refutation scales with weakness
      const refuteP = 0.55 - confidence * 0.35;
      if (Math.random() < refuteP) {
        const reason = REFUTATIONS[Math.floor(Math.random() * REFUTATIONS.length)];
        confidence = Math.max(0, confidence - rand(0.08, 0.22));
        rationale = rationale ? `${rationale}; ${reason}` : reason;
      } else {
        // Survived a round → small confidence boost
        confidence = Math.min(1, confidence + rand(0.02, 0.08));
      }
    }

    if (confidence < cfg.critic.rejectClaimThreshold) status = "rejected";
    else if (confidence < cfg.critic.weakClaimThreshold) status = "weakened";
    else status = "accepted";

    const updated: Claim = { ...c, confidence, status, rationale };
    out.push(updated);
    await args.onClaim?.(updated);
  }
  return out;
}

// Self-consistency: re-sample N times; only keep claims that converge.
export async function selfConsistencyVote(
  claims: Claim[],
  signal?: AbortSignal,
  config: ResearchConfig = DEFAULT_CONFIG,
): Promise<Claim[]> {
  const samples = config.selfConsistency.samples;
  const out: Claim[] = [];
  for (const c of claims) {
    if (c.status === "rejected") {
      out.push(c);
      continue;
    }
    let agree = 0;
    for (let i = 0; i < samples; i++) {
      await sleep(rand(20, 60), signal);
      // Higher confidence -> higher agreement probability
      if (Math.random() < 0.4 + c.confidence * 0.55) agree++;
    }
    const ratio = agree / samples;
    const updated: Claim = {
      ...c,
      sampledN: samples,
      agreedN: agree,
      confidence: ratio < config.selfConsistency.minAgreeRatio ? Math.min(c.confidence, ratio) : c.confidence,
      status: ratio < config.selfConsistency.minAgreeRatio
        ? (c.status === "accepted" ? "weakened" : c.status)
        : c.status,
      rationale:
        ratio < config.selfConsistency.minAgreeRatio
          ? (c.rationale ? `${c.rationale}; low self-consistency (${agree}/${samples})` : `low self-consistency (${agree}/${samples})`)
          : c.rationale,
    };
    out.push(updated);
  }
  return out;
}

export function isClaimCritical(c: Claim): boolean {
  // Heuristic: claims with strong assertive verbs are critical
  return /\b(prove|guarantee|always|never|outperforms|solves|eliminates|state-of-the-art)\b/i.test(c.text);
}
